import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import DeleteMotorbikeModal from '../molecules/DeleteMotorbikeModal'
import VehiclesEmptyState from '../molecules/VehiclesEmptyState'
import VehiclesFilterBar from '../molecules/VehiclesFilterBar'
import VehicleTableRow from '../molecules/VehicleTableRow'
import TablePagination from '../molecules/TablePagination'
import { FETCH_BRANDS } from 'src/redux/brandsReducer'
import {
  FETCH_MOTORBIKES,
  SET_CURRENT_PAGE,
  SET_STOCK_TAB,
  DELETE_MOTORBIKE,
  SET_MOTORBIKE_STATUS,
  MarketPlaceMotorbike,
  MotorbikeStatus
} from 'src/redux/motorbikesReducer'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducer } from 'src/redux/rootReducer'
import { formatPrice } from 'src/helpers/formatPrice'

const formatRegistrationYear = (firstRegistration: string | null): string => {
  if (firstRegistration === null || firstRegistration.length === 0) {
    return '-'
  }
  const year = firstRegistration.slice(0, 4)
  return /^\d{4}$/.test(year) ? year : '-'
}

const formatMileage = (mileage: number | null): string => {
  if (mileage === null) {
    return '-'
  }
  return `${formatPrice(mileage)} km`
}

const formatVin = (vin: string | null): string => {
  if (vin === null || vin.length === 0) {
    return '[VIN]'
  }
  return vin
}

const Vehicles = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { brands } = useSelector((state: RootReducer) => state.brandsReducer)
  const motorbikes = useSelector((state: RootReducer) => state.motorbikesReducer.motorbikes)
  const currentPage = useSelector((state: RootReducer) => state.motorbikesReducer.currentPage)
  const totalPages = useSelector((state: RootReducer) => state.motorbikesReducer.totalPages)
  const stockTab = useSelector((state: RootReducer) => state.motorbikesReducer.stockTab)

  const [showDeleteForm, setShowDeleteForm] = useState(false)
  const [selectedMotorbike, setSelectedMotorbike] = useState<MarketPlaceMotorbike | null>(null)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  useEffect(() => {
    dispatch(FETCH_BRANDS())
    dispatch(FETCH_MOTORBIKES())
  }, [dispatch, currentPage, stockTab])

  const handleAddMotorbike = (): void => {
    navigate('/selling/vehicles/new')
  }

  const handleDeleteMotorbike = (): void => {
    if (selectedMotorbike !== null) {
      dispatch(DELETE_MOTORBIKE(selectedMotorbike.id))
    }
    setShowDeleteForm(false)
    setSelectedMotorbike(null)
  }

  const handlePageChange = (page: number): void => {
    dispatch(SET_CURRENT_PAGE(page))
  }

  const handleStockTabChange = (tab: MotorbikeStatus): void => {
    setOpenMenuId(null)
    dispatch(SET_STOCK_TAB(tab))
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <VehiclesFilterBar
          stockTab={stockTab}
          onStockTabChange={handleStockTabChange}
          onAddVehicle={handleAddMotorbike}
        />

        <div className="self-stretch flex-1 relative bg-backgrounds-bg-field-component rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base flex flex-col justify-start items-start overflow-hidden">
          <div className="self-stretch px-6 py-2 bg-backgrounds-bg-disabled inline-flex justify-center items-center gap-3">
            <div className="flex-1 self-stretch flex justify-start items-center gap-3">
              <div className="flex-1 self-stretch flex justify-start items-center overflow-hidden">
                <div className="justify-start text-foregrounds-fg-muted text-xs font-medium font-Montserrat leading-5">
                  {t('my_vehicles.vehicle')}
                </div>
              </div>
              <div className="self-stretch flex justify-end items-center overflow-hidden">
                <div className="justify-start text-foregrounds-fg-muted text-xs font-medium font-Montserrat leading-5">
                  {t('my_vehicles.actions')}
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex-1 flex flex-col justify-start items-start overflow-y-auto">
            {motorbikes.length === 0 && <VehiclesEmptyState stockTab={stockTab} onAddVehicle={handleAddMotorbike} />}
            {motorbikes.map((motorbike) => {
              const brand = brands.find((item) => item.id === motorbike.brand_id)
              const brandName = brand?.name ?? motorbike.brand_name
              return (
                <VehicleTableRow
                  key={motorbike.id}
                  title={`${brandName} ${motorbike.model}`.trim()}
                  year={formatRegistrationYear(motorbike.first_registration)}
                  mileage={formatMileage(motorbike.total_mileage)}
                  vin={formatVin(motorbike.vin)}
                  status={motorbike.status}
                  isMenuOpen={openMenuId === motorbike.id}
                  onToggleMenu={() => {
                    setOpenMenuId(openMenuId === motorbike.id ? null : motorbike.id)
                  }}
                  onCloseMenu={() => {
                    setOpenMenuId(null)
                  }}
                  onMarkAsSold={() => {
                    setOpenMenuId(null)
                    dispatch(
                      SET_MOTORBIKE_STATUS({
                        id: motorbike.id,
                        status: 'sold',
                        name: `${brandName} ${motorbike.model}`.trim()
                      })
                    )
                  }}
                  onMarkAsInStock={() => {
                    setOpenMenuId(null)
                    dispatch(
                      SET_MOTORBIKE_STATUS({
                        id: motorbike.id,
                        status: 'in_stock',
                        name: `${brandName} ${motorbike.model}`.trim()
                      })
                    )
                  }}
                  onEdit={() => {
                    navigate(`/selling/vehicles/${motorbike.id}/edit`)
                  }}
                  onDelete={() => {
                    setSelectedMotorbike(motorbike)
                    setShowDeleteForm(true)
                  }}
                  onOpenDetails={() => {
                    navigate(`/selling/vehicles/${motorbike.id}`)
                  }}
                />
              )
            })}
          </div>

          <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>

        <DeleteMotorbikeModal
          isOpen={showDeleteForm}
          onClose={() => {
            setShowDeleteForm(false)
            setSelectedMotorbike(null)
          }}
          onConfirm={handleDeleteMotorbike}
          motorbike={selectedMotorbike}
          brands={brands}
        />
      </div>
    </Layout>
  )
}

export default Vehicles
